import _ from 'lodash'
import 'foundation-sites'
import Vue from 'vue'
import Component from 'vue-class-component'
import { Store } from 'vuex'
import RootState from '../../store/state'
import { Actions, Getters, Mutations } from '../../store/manager/helpers'
import { GetContentListRequest, GetContentListResponse } from '../../api/gerbera/contents'
import { CreateNewArticleConfigData, PublishConfigData } from '../dialog/PublishConfigInterface'
import { ModalState } from '../../store/manager/state'
import { Watch } from 'vue-property-decorator'

import ContentsHeader from './ContentsHeader.vue'
import ContentsNav from './ContentsNav.vue'
import ArticlesHeader from './ArticlesHeader.vue'
import ContentsToolBar from './ContentsToolBar.vue'
import ArticlesManage from './ArticlesManage.vue'

import ArticleCopy from '../dialog/ArticleCopy.vue'
import PublishConfig from '../dialog/PublishConfig.vue'
import ArticleDelete from '../dialog/ArticleDelete.vue'
import ChangeCategory from '../dialog/ChangeCategory.vue'
import ChangePublished from '../dialog/ChangePublished.vue'
import SelectedItemDelete from '../dialog/SelectedItemDelete.vue'
import CreateNewArticle from '../dialog/CreateNewArticle.vue'
import GetSiteUrl from '../dialog/GetSiteUrl.vue'
import * as CommonHelpers from '../../store/common/helpers'
import retryPromise, { createRetryHandler, RetryHandler } from '../../misc/retry-promise'
import { isSizeLimitExceeded, isUnauthorized } from '../../misc/get-status-code'
import { Dictionary } from 'vue-router/types/router'
import { ContentOrderBy, ContentSortKey, isContentSortKey, isPublishStatus, PublishStatus } from '../../api/gerbera/core'

@Component({
  components: {
    'contents-header': ContentsHeader,
    'contents-nav': ContentsNav,
    'articles-header': ArticlesHeader,
    'contents-toolbar': ContentsToolBar,
    'articles-manage': ArticlesManage,
    'article-copy': ArticleCopy,
    'publish-config': PublishConfig,
    'change-category': ChangeCategory,
    'change-published': ChangePublished,
    'article-delete': ArticleDelete,
    'selected-item-delete': SelectedItemDelete,
    'create-new-article': CreateNewArticle,
    'get-site-url': GetSiteUrl
  }
})
export default class ContentsManage extends Vue {
  name: 'ContentsManage'
  $store: Store<RootState>
  isModalVisible: boolean = false
  isSiteUrl: boolean = false
  retryHandler: RetryHandler = createRetryHandler()

  get siteId(): number {
    return Getters.getAcountData(this.$store).kajikiId
  }

  get siteUrlState() {
    return this.$store.state.manager.siteUrlModel
  }

  get modalState() {
    return this.$store.state.manager.modalState
  }

  get selectItemId() {
    return Getters.selectedArticleIds(this.$store)
  }

  get isSelectItemId(): boolean {
    return this.selectItemId.length > 0
  }

  get query(): Dictionary<string> {
    return this.$route.query
  }

  get page(): number {
    return parseInt(this.query.page, 10) || 1
  }

  get perPage(): number {
    return 20
  }

  get categoryId(): number | null | undefined {
    const categoryId = this.query.category_id

    if (categoryId === 'uncategorized') {
      return null
    }
    return parseInt(categoryId, 10) || undefined
  }

  get status(): PublishStatus | undefined {
    const status = this.query.status
    return isPublishStatus(status) ? status : undefined
  }

  get title(): string | undefined {
    return this.query.keyword
  }

  get sortKey(): ContentSortKey | undefined {
    const sortKey = this.query.sortby
    return isContentSortKey(sortKey) ? sortKey : undefined
  }

  get isDescending(): boolean | undefined {
    const order = this.query.order
    if (order === 'asc') {
      return false
    }
    if (order === 'desc') {
      return true
    }
    return undefined
  }

  get orderBy(): ContentOrderBy[] {
    if (_.isUndefined(this.sortKey) && _.isUndefined(this.isDescending)) {
      return [{
        key: 'modifiedDate',
        isDescending: true
      }]
    }
    return [{
      key: this.sortKey || 'modifiedDate',
      isDescending: _.isUndefined(this.isDescending) ? false : this.isDescending
    }]
  }

  get searchCondition(): GetContentListRequest {
    return {
      siteId: this.siteId,
      page: this.page,
      perPage: this.perPage,
      categoryId: this.categoryId,
      status: this.status,
      title: this.title,
      orderBy: this.orderBy
    }
  }

  get isLoadingKajiki(): boolean {
    return Getters.isLoadingKajiki(this.$store)
  }

  created(): void {
    Mutations.setLoadingArticle(this.$store, true)
  }

  mounted(): void {
    this.loadArticles()
  }

  @Watch('isLoadingKajiki')
  @Watch('searchCondition')
  loadArticles(): void {
    if (this.isLoadingKajiki || !this.siteId) {
      return
    }
    const searchCondition = Object.assign({}, this.searchCondition, <GetContentListRequest>{
      siteId: this.siteId
    })
    Mutations.setLoadingArticle(this.$store, true)
    this.requestArticles(searchCondition).then(() => {
      Mutations.setLoadingArticle(this.$store, false)
    }).then(() => {
      return this.requestStorageData()
    }).then(() => {
      return this.requestCategories()
    }).then(() => {
      return this.requestCategoryWhole()
    }).catch((reason) => {
      if (isUnauthorized(reason)) {
        CommonHelpers.Mutations.setErrorStatus(this.$store, 'session-timeout')
        return
      }
      CommonHelpers.Actions.addNotification(this.$store, {
        type: 'content-manage--search',
        level: 'warning',
        message: '検索に失敗しました。通信環境をご確認のうえ、もう一度お試しください。'
      })
    })
  }

  requestArticles(searchCondition: GetContentListRequest): Promise<GetContentListResponse> {
    return retryPromise((retry, count) => {
      return Actions.loadArticles(this.$store, searchCondition)
          .catch((reason) => {
            return this.retryHandler(reason, retry, count)
          })
    })
  }

  requestStorageData(): Promise<void> {
    return retryPromise((retry, count) => {
      return Actions.loadStorageData(this.$store)
          .catch((reason) => {
            return this.retryHandler(reason, retry, count)
          })
    })
  }

  requestCategories(): Promise<void> {
    return retryPromise((retry, count) => {
      return Actions.loadCategories(this.$store, {
        siteId: this.siteId,
        orderBy: [{
          key: 'menuOrder'
        }]
      }).catch((reason) => {
        return this.retryHandler(reason, retry, count)
      })
    })
  }

  requestCategoryWhole(): Promise<void> {
    return retryPromise((retry, count) => {
      return Actions.loadCategoryWhole(this.$store, this.siteId)
          .catch((reason) => {
            return this.retryHandler(reason, retry, count)
          })
    })
  }

  openModal(componentName: string) {
    this.isSiteUrl = componentName === 'get-site-url'
    this.isModalVisible = true
  }

  closeModal(): void {
    this.isModalVisible = false
  }

  // TODO:別コンポーネントに切り出す
  submitData(submitConfigData: PublishConfigData) {
    if (!this.modalState) {
      throw new Error('modalStateを取得できませんでした')
    }
    if (this.modalState.modalName === 'article-copy') {
      return this.submitCopyArticle(this.modalState)
    }
    if (this.modalState.modalName === 'publish-config') {
      return this.submitUpdateArticle(this.modalState, submitConfigData)
    }
    if (this.modalState.modalName === 'article-delete') {
      return this.submitDeleteArticle(this.modalState)
    }
    if (this.modalState.modalName === 'change-category' || this.modalState.modalName === 'change-published') {
      return this.submitUpdateSelectedItem(submitConfigData)
    }
    if (this.modalState.modalName === 'selected-item-delete') {
      return this.submitDeleteSelectedItem()
    }
    if (this.modalState.modalName === 'create-new-article') {
      return this.submitCreateNewArticle(submitConfigData)
    }
  }

  submitCreateNewArticle(submitConfigData: CreateNewArticleConfigData) {
    const categoryId = submitConfigData.category.categoryId
    this.$router.push({
      name: 'editor-new',
      params: {kajiki_id: this.siteId.toString()},
      query: {
        title: submitConfigData.title || '',
        category_id: categoryId ? categoryId.toString() : ''
      }
    })
  }

  submitCopyArticle(modalState: ModalState) {
    this.closeModal()
    retryPromise<any>((retry, count) => {
      if (!modalState.data || !modalState.data.contentId) {
        throw new Error('コンテンツIDを取得できませんでした')
      }
      return Actions.copyContentItem(this.$store, {
        siteId: this.siteId,
        contentId: modalState.data.contentId
      }).catch((reason) => {
        return this.retryHandler(reason, retry, count)
      })
    }).then(() => {
      // 記事一覧を再読込
      this.loadArticles()
      CommonHelpers.Actions.addNotification(this.$store, {
        type: 'content-manage--update-content',
        message: '記事をコピーしました。'
      })
    }).catch((reason) => {
      if (isUnauthorized(reason)) {
        CommonHelpers.Mutations.setErrorStatus(this.$store, 'session-timeout')
        return
      }
      if (isSizeLimitExceeded(reason)) {
        CommonHelpers.Actions.addNotification(this.$store, {
          type: 'content-manage--update-content',
          level: 'warning',
          message: 'ご契約中のプランで利用可能なデータ容量を超えました。\nこれ以上記事を保存できません。\n容量プランの変更には、いい生活 担当営業までご連絡ください。'
        })
        return
      }
      CommonHelpers.Actions.addNotification(this.$store, {
        type: 'content-manage--update-content',
        level: 'warning',
        message: '記事のコピーに失敗しました。通信環境をご確認のうえ、もう一度お試しください。'
      })
    })
  }

  submitUpdateArticle(modalState: ModalState, submitConfigData: PublishConfigData) {
    this.closeModal()
    if (!modalState.data || !modalState.data.contentId) {
      throw new Error('コンテンツIDを取得できませんでした')
    }
    const params = Object.assign({
      siteId: this.siteId,
      contentId: [modalState.data.contentId]
    }, submitConfigData)

    retryPromise<any>((retry, count) => {
      return Actions.updateContentItem(this.$store, params)
          .catch((reason) => {
            return this.retryHandler(reason, retry, count)
          })
    }).then(() => {
      // 記事一覧を再読込
      this.loadArticles()
      CommonHelpers.Actions.addNotification(this.$store, {
        type: 'content-manage--update-content',
        message: `記事を更新しました。`
      })
    }).catch((reason) => {
      if (isUnauthorized(reason)) {
        CommonHelpers.Mutations.setErrorStatus(this.$store, 'session-timeout')
        return
      }
      CommonHelpers.Actions.addNotification(this.$store, {
        type: 'content-manage--update-content',
        level: 'warning',
        message: '記事の更新に失敗しました。通信環境をご確認のうえ、もう一度お試しください。'
      })
    })
  }

  submitUpdateSelectedItem(submitConfigData: PublishConfigData) {
    this.closeModal()
    retryPromise<any>((retry, count) => {
      return Actions.selectedItemUpdate(this.$store, submitConfigData)
          .catch((reason) => {
            return this.retryHandler(reason, retry, count)
          })
    }).then((count) => {
      // 記事一覧を再読込
      this.loadArticles()
      CommonHelpers.Actions.addNotification(this.$store, {
        type: 'content-manage--update-content',
        message: `${count} 件の記事を更新しました。`
      })
    }).catch((reason) => {
      if (isUnauthorized(reason)) {
        CommonHelpers.Mutations.setErrorStatus(this.$store, 'session-timeout')
        return
      }
      CommonHelpers.Actions.addNotification(this.$store, {
        type: 'content-manage--update-content',
        level: 'warning',
        message: '記事の更新に失敗しました。通信環境をご確認のうえ、もう一度お試しください。'
      })
    })
  }

  submitDeleteArticle(modalState: ModalState) {
    this.closeModal()
    retryPromise<any>((retry, count) => {
      if (!modalState.data || !modalState.data.contentId) {
        throw new Error('コンテンツIDを取得できませんでした')
      }
      return Actions.deleteContentItem(this.$store, {
        siteId: this.siteId,
        contentId: [modalState.data.contentId]
      }).catch((reason) => {
        return this.retryHandler(reason, retry, count)
      })
    }).then(() => {
      // 記事一覧を再読込
      this.loadArticles()
      CommonHelpers.Actions.addNotification(this.$store, {
        type: 'content-manage--update-content',
        message: `記事を削除しました。`
      })
    }).catch((reason) => {
      if (isUnauthorized(reason)) {
        CommonHelpers.Mutations.setErrorStatus(this.$store, 'session-timeout')
        return
      }
      CommonHelpers.Actions.addNotification(this.$store, {
        type: 'content-manage--update-content',
        level: 'warning',
        message: '記事の削除に失敗しました。通信環境をご確認のうえ、もう一度お試しください。'
      })
    })
  }

  submitDeleteSelectedItem() {
    this.closeModal()
    retryPromise<any>((retry, count) => {
      return Actions.selectedItemDelete(this.$store)
          .catch((reason) => {
            return this.retryHandler(reason, retry, count)
          })
    }).then((count) => {
      // 記事一覧を再読込
      this.loadArticles()
      CommonHelpers.Actions.addNotification(this.$store, {
        type: 'content-manage--update-content',
        message: `${count} 件の記事を削除しました。`
      })
    }).catch((reason) => {
      if (isUnauthorized(reason)) {
        CommonHelpers.Mutations.setErrorStatus(this.$store, 'session-timeout')
        return
      }
      CommonHelpers.Actions.addNotification(this.$store, {
        type: 'content-manage--update-content',
        level: 'warning',
        message: '記事の削除に失敗しました。通信環境をご確認のうえ、もう一度お試しください。'
      })
    })
  }
}
